/**
 * Ticket de texto monoespaciado para impresoras térmicas ESC/POS (58/80 mm).
 * Evita convertir HTML/CSS (origen del formato roto en "escalera").
 */

export function resolvePaperWidth(preferred) {
	let w = String(preferred || '').trim();
	if (!w) {
		try {
			w =
				localStorage.getItem('poskn_ancho_impresora') ||
				localStorage.getItem('pos_ticket_formato') ||
				'';
		} catch (e) {
			w = '';
		}
	}
	if (w === '2') w = '58';
	if (w === '3') w = '80';
	if (w !== '58' && w !== '80') w = '80';
	return w;
}

export function thermalCols(paperWidth) {
	return resolvePaperWidth(paperWidth) === '58' ? 32 : 42;
}

function fit(str, n) {
	const s = String(str || '').replace(/\s+/g, ' ').trim();
	if (s.length <= n) return s;
	return s.slice(0, Math.max(0, n - 1)) + '.';
}

function center(cols, str) {
	const s = fit(str, cols);
	const pad = Math.max(0, Math.floor((cols - s.length) / 2));
	return ' '.repeat(pad) + s;
}

function wrap(cols, str) {
	const raw = String(str || '').replace(/\s+/g, ' ').trim();
	if (!raw) return [];
	const out = [];
	let rest = raw;
	while (rest.length > cols) {
		let cut = rest.lastIndexOf(' ', cols);
		if (cut < Math.floor(cols / 2)) cut = cols;
		out.push(rest.slice(0, cut).trimEnd());
		rest = rest.slice(cut).trimStart();
	}
	if (rest) out.push(rest);
	return out;
}

function leftRight(cols, left, right) {
	const L = String(left || '');
	const R = String(right || '');
	const gap = cols - L.length - R.length;
	if (gap >= 1) return [L + ' '.repeat(gap) + R];
	const lines = wrap(cols, L);
	if (!R) return lines.length ? lines : [''];
	if (lines.length === 0) return [fit(R, cols)];
	const last = lines[lines.length - 1];
	const g2 = cols - last.length - R.length;
	if (g2 >= 1) {
		lines[lines.length - 1] = last + ' '.repeat(g2) + R;
		return lines;
	}
	return lines.concat(wrap(cols, R));
}

function sep(cols, ch) {
	return String(ch || '-').repeat(cols);
}

/**
 * @param {object} data
 * @param {string} [data.paperWidth] 58|80
 * @param {string} [data.companyName]
 * @param {string} [data.companyDocument]
 * @param {string} [data.companyAddress]
 * @param {string} [data.companyPhones]
 * @param {string} [data.companySocial]
 * @param {string} [data.companyFooter]
 * @param {boolean} [data.showSystem]
 * @param {number|string} [data.tasa]
 * @param {object} pedido historial_pedidos item
 */
export function buildThermalTicketText(data) {
	const pedido = data.pedido || {};
	const cols = thermalCols(data.paperWidth);
	const lines = [];
	const push = (s) => lines.push(s);
	const pushWrap = (s) => wrap(cols, s).forEach(push);
	const pushCenter = (s) => wrap(cols, s).forEach((w) => push(center(cols, w)));

	const company = data.companyName || '';
	if (company) pushCenter(String(company).toUpperCase());
	if (data.companyDocument) pushCenter(data.companyDocument);
	if (data.companyAddress) pushCenter(data.companyAddress);
	if (data.companyPhones) pushCenter(data.companyPhones);
	push(sep(cols, '='));

	const numero = pedido.numero || '';
	push(center(cols, `# TICKET ${numero}`));
	const wc = pedido.wc_order_number || '-';
	push(center(cols, `WC: ${wc}`));
	const nPedido = pedido.wc_order_id || pedido.wc_order_number || wc || numero;
	pushWrap(`Nº pedido: ${nPedido}`);

	const fecha = data.fecha || new Date();
	const fechaStr =
		typeof fecha.toLocaleDateString === 'function'
			? `${fecha.toLocaleDateString()} ${fecha.toLocaleTimeString()}`
			: String(fecha);
	pushCenter(fechaStr);

	const atendido =
		(pedido.ejecutor && pedido.ejecutor.nombre) ||
		(pedido.vendedor && pedido.vendedor.nombre) ||
		'';
	if (atendido) pushWrap(`Atendido por: ${atendido}`);

	const tipo = pedido.tipo_servicio_label || data.tipoServicioLabel || '';
	if (tipo) {
		push('');
		pushCenter(String(tipo).toUpperCase());
	}

	const cli = pedido.cliente || {};
	if (cli.nombre || cli.documento) {
		push(sep(cols));
		pushCenter('DATOS DEL CLIENTE');
		if (cli.documento) pushWrap(`DOC: ${cli.documento}`);
		if (cli.nombre) pushWrap(`NOMBRE: ${cli.nombre}`);
		if (cli.telefono) pushWrap(`TEL: ${cli.telefono}`);
		if (cli.direccion) pushWrap(`DIR: ${cli.direccion}`);
	}

	push(sep(cols));
	leftRight(cols, 'PRODUCTO', 'CANT|PRECIO').forEach(push);
	push(sep(cols));

	const items = Array.isArray(pedido.items) ? pedido.items : [];
	items.forEach((item) => {
		const name = item.name || item.nombre || '';
		wrap(cols, name).forEach(push);
		const opts = (
			Array.isArray(item.ckn_options)
				? item.ckn_options
				: Array.isArray(item.cknNames)
					? item.cknNames
					: []
		)
			.map((o) => (typeof o === 'string' || typeof o === 'number' ? String(o) : ''))
			.filter((o) => o && o !== 'Array');
		opts.forEach((o) => pushWrap(`- ${o}`));
		if (item.notas) pushWrap(`* ${item.notas}`);
		const qty = item.cantidad != null ? item.cantidad : item.qty || 1;
		const price = Number(item.price || 0) * Number(qty);
		leftRight(cols, `x${qty}`, `$${price.toFixed(2)}`).forEach(push);
		push('');
	});

	push(sep(cols));
	const subtotal = Number(pedido.subtotal != null ? pedido.subtotal : 0);
	leftRight(cols, 'Subtotal:', `$${subtotal.toFixed(2)}`).forEach(push);
	const desc = Number((pedido.descuento && pedido.descuento.monto) || 0);
	if (desc > 0) leftRight(cols, 'Descuento:', `-$${desc.toFixed(2)}`).forEach(push);
	const delivery = Number(pedido.delivery_fee || 0);
	if (delivery > 0) leftRight(cols, 'Delivery:', `+$${delivery.toFixed(2)}`).forEach(push);

	const totalUsd = Number((pedido.total && pedido.total.usd) || 0);
	const totalBs = Number((pedido.total && pedido.total.bs) || 0);
	push(sep(cols, '='));
	leftRight(cols, 'TOTAL:', `$${totalUsd.toFixed(2)}`).forEach(push);
	leftRight(cols, 'Bs.', totalBs.toFixed(2)).forEach(push);

	const metodos = Array.isArray(pedido.metodosPago) ? pedido.metodosPago : [];
	if (metodos.length) {
		push(sep(cols));
		push('PAGO:');
		metodos.forEach((m) => {
			const metodoRaw = String(m.metodo || '');
			const metodo = metodoRaw.replace(/-/g, ' ').toUpperCase();
			const esCredito = /credito/i.test(metodoRaw);
			const esBs =
				typeof data.metodoEsBs === 'function'
					? data.metodoEsBs(m.metodo)
					: /bs|bolivar|pago.?movil|transfer/i.test(metodoRaw);
			const sim = esBs ? 'Bs. ' : '$';
			const tasaTk = Number(pedido.tasa || data.tasa || 1) || 1;
			let montoPrint = Number(m.monto || 0);
			if (esCredito && montoPrint < 0.01) {
				const totalUsd = Number((pedido.total && pedido.total.usd) || 0);
				let pagadoOtros = 0;
				metodos.forEach((x) => {
					if (/credito/i.test(String(x.metodo || ''))) return;
					const xBs =
						typeof data.metodoEsBs === 'function'
							? data.metodoEsBs(x.metodo)
							: /bs|bolivar/i.test(String(x.metodo || ''));
					pagadoOtros += xBs ? Number(x.monto || 0) / tasaTk : Number(x.monto || 0);
				});
				montoPrint = Math.max(0, totalUsd - pagadoOtros);
			}
			leftRight(cols, metodo + ':', `${sim}${montoPrint.toFixed(2)}`).forEach(push);
			if (esCredito) {
				leftRight(cols, 'CREDITO Bs.', (montoPrint * tasaTk).toFixed(2)).forEach(push);
			}
			if (m.referencia) pushWrap(`Ref: ${m.referencia}`);
		});
	}

	push(sep(cols));
	pushCenter('Gracias por su compra!');
	if (data.companySocial) pushCenter(data.companySocial);
	if (data.companyFooter) pushCenter(data.companyFooter);
	if (data.showSystem) pushCenter('POSKN - ServiKN v7');
	push('');
	push('');

	return lines.join('\n');
}

/**
 * Comanda de cocina en texto monoespaciado (print agent ESC/POS).
 * @param {object} orderData respuesta de poskn_get_order_receipt
 * @param {object} [opts]
 * @param {string} [opts.paperWidth] 58|80
 * @param {string} [opts.companyName]
 */
export function buildThermalComandaText(orderData, opts) {
	const data = orderData || {};
	const o = opts || {};
	const cols = thermalCols(o.paperWidth);
	const lines = [];
	const push = (s) => lines.push(s);
	const pushWrap = (s) => wrap(cols, s).forEach(push);
	const pushCenter = (s) => wrap(cols, s).forEach((w) => push(center(cols, w)));

	pushCenter('*** COMANDA ***');
	if (o.companyName) pushCenter(String(o.companyName).toUpperCase());
	push(sep(cols, '='));

	pushCenter(`COMANDA #${data.id || ''}`);
	if (data.date) pushWrap(`Fecha: ${data.date}`);

	if (data.mesa_numero) {
		const n = String(data.mesa_numero || '').trim();
		const m = String(data.mesa_nombre || '').trim();
		const mesa = m && m !== n ? `${n} - ${m}` : n || m;
		pushCenter(`MESA: ${mesa}`);
	}

	if (data.tipo_servicio_label) {
		push('');
		pushCenter(String(data.tipo_servicio_label).toUpperCase());
	}

	const delivery = parseFloat(data.delivery_fee);
	if (delivery > 0) pushCenter(`DELIVERY: $${delivery.toFixed(2)}`);

	pushWrap(`Cliente: ${data.customer || 'Cliente General'}`);
	if (data.atendido_por) pushWrap(`Atendido por: ${data.atendido_por}`);

	push(sep(cols));
	pushCenter('PRODUCTOS');
	push(sep(cols));

	const items = Array.isArray(data.items) ? data.items : [];
	items.forEach((item) => {
		const qty = item.qty != null ? item.qty : item.cantidad || 1;
		const name = String(item.name || item.nombre || '').trim();
		const nuevo = item.is_new ? ' [NUEVO]' : '';
		pushWrap(`${qty}x ${name}${nuevo}`);

		const optsItem = (
			Array.isArray(item.ckn_options)
				? item.ckn_options
				: Array.isArray(item.cknNames)
					? item.cknNames
					: []
		)
			.map((x) => (typeof x === 'string' || typeof x === 'number' ? String(x) : ''))
			.filter((x) => x && x !== 'Array');
		optsItem.forEach((x) => pushWrap(`  - ${x}`));

		const note = String(item.note || item.notes || '').trim();
		if (note) pushWrap(`  * NOTA: ${note}`);
		push('');
	});

	push(sep(cols));
	const total = Number(data.total_numeric != null ? data.total_numeric : data.total || 0);
	leftRight(cols, 'TOTAL:', `$${total.toFixed(2)}`).forEach(push);

	const now = new Date();
	pushCenter(`${now.toLocaleDateString()} ${now.toLocaleTimeString()}`);
	push('');
	push('');

	return lines.join('\n');
}

/** WooCommerce wc_price / HTML → número. */
export function parseTicketMoney(value) {
	if (typeof value === 'number' && Number.isFinite(value)) return value;
	let str = String(value == null ? '' : value);
	str = str.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();
	str = str.replace(/[^\d,.\-]/g, '');
	if (!str) return 0;
	const lastComma = str.lastIndexOf(',');
	const lastDot = str.lastIndexOf('.');
	if (lastComma > -1 && lastDot > -1) {
		if (lastComma > lastDot) {
			return parseFloat(str.replace(/\./g, '').replace(',', '.')) || 0;
		}
		return parseFloat(str.replace(/,/g, '')) || 0;
	}
	if (lastComma > -1) {
		const parts = str.split(',');
		if (parts[1] && parts[1].length === 3 && parts[0].replace(/-/g, '').length > 0) {
			return parseFloat(str.replace(/,/g, '')) || 0;
		}
		return parseFloat(str.replace(',', '.')) || 0;
	}
	return parseFloat(str) || 0;
}

function stripTags(value) {
	return String(value == null ? '' : value)
		.replace(/<[^>]+>/g, ' ')
		.replace(/&nbsp;/gi, ' ')
		.replace(/&amp;/g, '&')
		.replace(/\s+/g, ' ')
		.trim();
}

/**
 * Recibo de pedido ya realizado (reimpresión desde Pedidos).
 * @param {object} receipt respuesta de poskn_get_order_receipt
 * @param {object} [opts]
 */
export function buildThermalReceiptText(receipt, opts) {
	const data = receipt || {};
	const o = opts || {};
	const cols = thermalCols(o.paperWidth);
	const lines = [];
	const push = (s) => lines.push(s);
	const pushWrap = (s) => wrap(cols, s).forEach(push);
	const pushCenter = (s) => wrap(cols, s).forEach((w) => push(center(cols, w)));

	if (o.companyName) pushCenter(String(o.companyName).toUpperCase());
	if (o.companyDocument) pushCenter(o.companyDocument);
	if (o.companyAddress) pushCenter(o.companyAddress);
	if (o.companyPhones) pushCenter(o.companyPhones);
	push(sep(cols, '='));

	pushCenter(`RECIBO #${data.id || ''}`);
	if (data.date) {
		pushCenter(String(data.date));
	} else {
		const now = new Date();
		pushCenter(`${now.toLocaleDateString()} ${now.toLocaleTimeString()}`);
	}

	if (data.tipo_servicio_label) {
		push('');
		pushCenter(String(data.tipo_servicio_label).toUpperCase());
	}

	push(sep(cols));
	pushWrap(`Cliente: ${data.customer || 'Cliente General'}`);
	if (data.status) pushWrap(`Estado: ${stripTags(data.status)}`);
	if (data.atendido_por) pushWrap(`Atendido por: ${data.atendido_por}`);
	if (data.payment) pushWrap(`Pago: ${stripTags(data.payment)}`);

	const pagos = Array.isArray(data.payment_details_full) ? data.payment_details_full : [];
	if (pagos.length) {
		push(sep(cols));
		push('PAGO:');
		pagos.forEach((p) => {
			const metodoRaw = String((p && p.metodo) || '');
			const metodo = metodoRaw.replace(/-/g, ' ').toUpperCase();
			const esBs =
				typeof o.metodoEsBs === 'function'
					? o.metodoEsBs(p.metodo)
					: /bs|bolivar|pago.?movil|transfer/i.test(metodoRaw);
			const sim = esBs ? 'Bs. ' : '$';
			const monto = Number((p && p.monto) || 0);
			leftRight(cols, metodo + ':', `${sim}${monto.toFixed(2)}`).forEach(push);
			if (p && p.referencia) pushWrap(`Ref: ${p.referencia}`);
		});
	}

	push(sep(cols));
	leftRight(cols, 'PRODUCTO', 'CANT|PRECIO').forEach(push);
	push(sep(cols));

	const items = Array.isArray(data.items) ? data.items : [];
	items.forEach((item) => {
		const name = String(item.name || item.nombre || '').trim();
		wrap(cols, name).forEach(push);
		const optsItem = (
			Array.isArray(item.ckn_options)
				? item.ckn_options
				: Array.isArray(item.cknNames)
					? item.cknNames
					: Array.isArray(item.composition)
						? item.composition
						: []
		)
			.map((x) => (typeof x === 'string' || typeof x === 'number' ? String(x) : ''))
			.filter((x) => x && x !== 'Array');
		optsItem.forEach((x) => pushWrap(`- ${x}`));
		const note = String(item.note || item.notes || '').trim();
		if (note) pushWrap(`* ${note}`);
		const qty = item.qty != null ? item.qty : item.cantidad || 1;
		const price =
			item.total_numeric != null && item.total_numeric !== ''
				? Number(item.total_numeric)
				: parseTicketMoney(item.total);
		leftRight(cols, `x${qty}`, `$${Number(price || 0).toFixed(2)}`).forEach(push);
		push('');
	});

	push(sep(cols));
	const subtotal =
		data.subtotal_numeric != null && data.subtotal_numeric !== ''
			? Number(data.subtotal_numeric)
			: parseTicketMoney(data.subtotal);
	leftRight(cols, 'Subtotal:', `$${Number(subtotal || 0).toFixed(2)}`).forEach(push);
	const delivery = Number(data.delivery_fee || 0);
	if (delivery > 0) leftRight(cols, 'Delivery:', `+$${delivery.toFixed(2)}`).forEach(push);

	const total =
		data.total_numeric != null && data.total_numeric !== ''
			? Number(data.total_numeric)
			: parseTicketMoney(data.total);
	push(sep(cols, '='));
	leftRight(cols, 'TOTAL:', `$${Number(total || 0).toFixed(2)}`).forEach(push);

	push(sep(cols));
	pushCenter('Gracias por su compra!');
	if (o.companySocial) pushCenter(o.companySocial);
	if (o.companyFooter) pushCenter(o.companyFooter);
	if (o.showSystem) pushCenter('POSKN - ServiKN v7');
	push('');
	push('');

	return lines.join('\n');
}

function escapeHtml(str) {
	return String(str == null ? '' : str)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

/**
 * Mismo ticket en HTML para diálogo, iframe, APK y WebView.
 * El contenido es el texto térmico; no hay otra plantilla.
 */
export function thermalTextToHtml(title, text, paperWidth) {
	const w = resolvePaperWidth(paperWidth);
	const mm = w === '58' ? 58 : 80;
	const font = w === '58' ? '11px' : '12px';
	const body = escapeHtml(text);
	const docTitle = escapeHtml(title || 'POSKN');
	return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${docTitle}</title>
<style id="poskn-dark-print">
@page{size:${mm}mm auto;margin:0;}
html,body{margin:0;padding:0;background:#fff;color:#000;}
.ticket{
  width:${mm}mm;max-width:100%;margin:0 auto;padding:2mm;box-sizing:border-box;
  font-family:'Courier New',Courier,monospace;font-size:${font};line-height:1.15;
  font-weight:700;color:#000;white-space:pre;overflow:hidden;
  -webkit-print-color-adjust:exact;print-color-adjust:exact;
}
</style></head><body><pre class="ticket">${body}</pre></body></html>`;
}
